import React from 'react';

import { useIntl } from 'react-intl';

import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { Popup } from '@atlaskit/editor-common/ui';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import type { ConfluencePageAri } from '@atlassian/ari';

import type { AgentManagedExtensionPlugin } from '../../common/agentManagedExtensionPluginType';
import { messages } from '../../common/messages';

import { ConfigurePanel } from './ConfigurePanel';
import { useAiManagedObject } from './useAiManagedObject';
import { useConfiguredAgentManagedExtension } from './useConfiguredAgentManagedExtension';
import { usePopupTargetElement } from './usePopupTargetElement';

interface AgentManagedExtensionConfigurePopupProps {
	api: ExtractInjectionAPI<AgentManagedExtensionPlugin> | undefined;
	editorView: EditorView;
	mountPoint?: HTMLElement;
	referenceObjectAri?: ConfluencePageAri;
}

const POPUP_STYLE = { width: 500 } as const;
const POPUP_OFFSET: [number, number] = [0, 8];

/**
 * Renders the inline configuration popup for the agent-managed extension
 * currently in (or around) the selection — when the plugin has been told to open the popup.
 */
export const AgentManagedExtensionConfigurePopup = ({
	api,
	editorView,
	mountPoint,
	referenceObjectAri,
}: AgentManagedExtensionConfigurePopupProps): React.JSX.Element | null => {
	const intl = useIntl();
	const configurationAriaLabel = intl.formatMessage(messages.configurationAriaLabel);
	const { aimoId, blockElement, cachedAimo, configLocalId, found, foundPos, isOpen } =
		useConfiguredAgentManagedExtension({ api, editorView });
	const targetElement = usePopupTargetElement({ blockElement, isOpen });
	const { aimoData, error, handleCancel, handleSave, initialPrompt, isSaving } = useAiManagedObject(
		{
			aimoId,
			cachedAimo,
			configLocalId,
			editorView,
			foundPos,
			isOpen,
			referenceObjectAri,
		},
	);

	if (!isOpen || !found || !targetElement) {
		return null;
	}

	return (
		<Popup
			alignX="center"
			alignY="bottom"
			target={targetElement}
			mountTo={mountPoint}
			zIndex={100}
			fitWidth={500}
			fitHeight={250}
			preventOverflow={true}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- `Popup` accepts only an inline style prop for its outer wrapper
			style={POPUP_STYLE}
			offset={POPUP_OFFSET}
			ariaLabel={configurationAriaLabel}
		>
			<ConfigurePanel
				initialPrompt={initialPrompt}
				onSave={handleSave}
				onCancel={handleCancel}
				isSaving={isSaving}
				error={error}
				aimoData={aimoData}
				cloudId={referenceObjectAri?.siteId}
			/>
		</Popup>
	);
};
